import { parseAsArrayOf, parseAsString, parseAsStringLiteral, useQueryState, useQueryStates } from 'nuqs';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { usePartnerConfig } from '~/context/PartnerConfigContext';
import { MarketplaceLead, MarketplaceSort, marketplaceSort } from '~/types/marketplace';
import { parseAsMarketplaceChannel } from '~/utils/channels';

type UserVendorsListParamsParams = {
  lead: MarketplaceLead | null;
};

export const useVendorsListUrlParams = ({ lead }: UserVendorsListParamsParams) => {
  const navigate = useNavigate();

  const config = usePartnerConfig();
  const [channel] = useQueryState('channel', parseAsMarketplaceChannel.withDefault('brighte'));
  const partner = config?.id;

  const [urlPostcode] = useQueryState('postcode');
  const activeChannel = config?.configuration?.channels?.find((c) => c.slug === channel);
  const fixedPostcode = activeChannel?.fixedPostcode;
  const defaultPostcode = fixedPostcode ?? lead?.postcode ?? '';

  // Channel config is the source of truth for the list sort default.
  // Falls back to RANKING (the former 50/50 flag rollout is now 100% ranked).
  const channelSort = activeChannel?.vendorSortingPreference;
  const defaultSort: MarketplaceSort = marketplaceSort.includes(channelSort as MarketplaceSort)
    ? (channelSort as MarketplaceSort)
    : 'RANKING';

  const [queryParams, setQueryParams] = useQueryStates({
    postcode: parseAsString.withDefault(defaultPostcode),
    suburb: parseAsString.withDefault(lead?.city ?? ''),
    manufacturerBrands: parseAsArrayOf(parseAsString).withDefault([]),
    categories: parseAsArrayOf(parseAsString).withDefault(lead?.categorySlugs ?? []),
    promotions: parseAsArrayOf(parseAsString).withDefault([]),
    sort: parseAsStringLiteral(marketplaceSort).withDefault(defaultSort),
    synthetic: parseAsString,
    leadId: parseAsString,
  });
  const { sort } = queryParams;

  useEffect(() => {
    let shouldNavigate = false;
    const params = new URLSearchParams(window.location.search);

    if (lead?.channel && lead.channel !== channel) {
      // Sync the URL channel param to lead.channel when they differ. Comparing
      // channels directly avoids spurious navigates when `partner` (from
      // PartnerConfigContext) is still undefined on the first render while
      // config is loading.
      params.set('channel', lead.channel);
      shouldNavigate = true;
    }

    // Set initial postcode if not in URL but available from lead or fixed postcode
    if (!urlPostcode && defaultPostcode) {
      setQueryParams(
        (current) => ({
          ...current,
          postcode: defaultPostcode,
          ...(lead?.city && !current.suburb && { suburb: lead.city }),
        }),
        { history: 'replace' },
      );
    }

    if (shouldNavigate) {
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [sort, lead?.channel, channel, partner, navigate, urlPostcode, defaultPostcode, setQueryParams, lead?.city]);

  return {
    channel,
    partner,
    defaultSort,
    ...queryParams,
    setQueryParams,
  };
};
